import React from "react";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  Eye, Users, Heart, Bookmark, Building2, Banknote, Calendar, BookOpen,
  HelpCircle, MessageSquare, Shell, UserCircle, TrendingUp, UserPlus,
} from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Loading } from "@/components/common/Loading";
import { StatCard } from "@/components/dashboard/StatCard";

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg bg-[hsl(var(--popover)/0.95)] px-3 py-2 text-xs shadow-lg backdrop-blur-xl">
      <div className="font-[600] text-foreground">{label}</div>
      <div className="text-muted-foreground">{payload[0].value} view{payload[0].value === 1 ? "" : "s"}</div>
    </div>
  );
};

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between py-2.5">
    <span className="flex items-center gap-2.5 text-sm text-foreground/80">
      <Icon className="h-4 w-4 text-muted-foreground" /> {label}
    </span>
    <span className="font-[640] text-foreground">{value}</span>
  </div>
);

const TYPE_LABELS = {
  personal: "Personal profile",
  business: "Businesses",
  capital: "Capital profiles",
  event: "Events",
  resource: "Resources",
};

export default function Analytics() {
  const { data, isLoading } = useQuery({
    queryKey: ["analytics-overview"],
    queryFn: async () => (await api.get("/analytics/overview")).data,
  });

  if (isLoading || !data) return <Loading label="Crunching your numbers" />;

  const { views, network, audience, content, engagement } = data;
  const series = views.series.map((p) => ({ ...p, label: format(parseISO(p.day), "MMM d") }));
  const byTypeEntries = Object.entries(views.by_type || {}).sort((a, b) => b[1] - a[1]);
  const peakDay = series.reduce((m, p) => (p.count > m.count ? p : m), { count: 0, label: "—" });

  return (
    <div data-testid="analytics-page">
      <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">Profile Analytics</h1>
      <p className="mt-1 text-muted-foreground">How members are discovering and engaging with you.</p>

      {/* Headline stats */}
      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Eye} label="Profile views (30d)" value={views.total_30d} sub={`${views.total_all_time} all-time`} testId="stat-views" />
        <StatCard icon={Users} label="Connections" value={network.connections} sub={`${network.pending_incoming} pending`} to="/dashboard/network" testId="stat-connections" />
        <StatCard icon={Heart} label="Fans" value={audience.fans} sub="Followers of your profiles" testId="stat-fans" />
        <StatCard icon={Bookmark} label="Saves received" value={audience.bookmarks_received} sub="Bookmarks by others" testId="stat-saves" />
      </div>

      {/* Views chart */}
      <Card className="mt-6 border-0 p-6" data-testid="views-chart-card">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-[640] text-foreground">Profile views</h2>
            <p className="text-sm text-muted-foreground">Unique daily views across all your profiles, last 30 days.</p>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <TrendingUp className="h-4 w-4 text-primary" /> Peak {peakDay.count} on {peakDay.label}
          </div>
        </div>
        <div className="mt-5 h-[260px] w-full">
          <ResponsiveContainer width="100%" height={260} minHeight={260} debounce={50}>
            <AreaChart data={series} margin={{ left: -18, right: 8, top: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="viewsFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#148b8f" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#148b8f" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(16,24,40,0.06)" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#667085" }} tickLine={false} axisLine={false} interval={6} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#667085" }} tickLine={false} axisLine={false} width={30} />
              <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#148b8f", strokeOpacity: 0.2 }} />
              <Area type="monotone" dataKey="count" stroke="#148b8f" strokeWidth={2.5} fill="url(#viewsFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {byTypeEntries.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {byTypeEntries.map(([t, c]) => (
              <span key={t} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-[560] text-foreground/80" data-testid={`views-bytype-${t}`}>
                {TYPE_LABELS[t] || t}: {c}
              </span>
            ))}
          </div>
        )}
      </Card>

      {/* Breakdown */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="border-0 p-6" data-testid="content-card">
          <h2 className="text-lg font-[640] text-foreground">Your published content</h2>
          <div className="mt-3 divide-y divide-border/70">
            <Row icon={UserCircle} label="Personal profile" value={content.published_personal ? "Live" : "Draft"} />
            <Row icon={Building2} label="Businesses" value={content.businesses} />
            <Row icon={Banknote} label="Capital profiles" value={content.capital} />
            <Row icon={Calendar} label="Events" value={content.events} />
            <Row icon={BookOpen} label="Resources" value={content.resources} />
          </div>
        </Card>

        <Card className="border-0 p-6" data-testid="engagement-card">
          <h2 className="text-lg font-[640] text-foreground">Engagement</h2>
          <div className="mt-3 divide-y divide-border/70">
            <Row icon={HelpCircle} label="Questions received" value={`${engagement.questions_received}${engagement.questions_unanswered ? ` · ${engagement.questions_unanswered} to answer` : ""}`} />
            <Row icon={MessageSquare} label="Chatter reactions" value={engagement.chatter_reactions} />
            <Row icon={MessageSquare} label="Chatter comments" value={engagement.chatter_comments} />
            <Row icon={Shell} label="Resource ratings" value={`${engagement.resource_ratings}${engagement.resource_ratings ? ` · ${engagement.resource_avg_rating}★ avg` : ""}`} />
            <Row icon={MessageSquare} label="Resource comments" value={engagement.resource_comments} />
            <Row icon={UserPlus} label="Profiles you follow" value={network.following} />
          </div>
        </Card>
      </div>
    </div>
  );
}
